import React, { useState } from 'react';
import { UserProfile } from '../../types';
import { Save, HardDrive, Download, Database } from 'lucide-react';
import { soundEffects } from '../../lib/soundEffects';

interface Props {
  currentUser: UserProfile;
  onUpdateProfile: (profile: Partial<UserProfile>) => void;
}

export const DataStorageSection: React.FC<Props> = ({ currentUser, onUpdateProfile }) => {
  const [data, setData] = useState<NonNullable<UserProfile['dataSettings']>>(
    currentUser.dataSettings || {
      autoDownloadMedia: 'wifi',
      mediaQuality: 'medium',
      dataSaver: false,
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearedSuccess, setClearedSuccess] = useState(false);

  const handleSave = async () => {
    soundEffects.playTap();
    setIsSaving(true);
    await onUpdateProfile({ dataSettings: data });
    setIsSaving(false);
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 2000);
  };

  const handleClearCache = () => {
    soundEffects.playTap();
    setIsClearing(true);
    setTimeout(() => {
      setIsClearing(false);
      setClearedSuccess(true);
      setTimeout(() => setClearedSuccess(false), 2000);
    }, 1500); // Simulate clearing cache
  };

  const updateSettings = (key: keyof typeof data, val: any) => {
    setData({ ...data, [key]: val });
  };

  const renderSelect = (label: string, key: keyof typeof data, options: {value: string, label: string}[]) => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-[#141c2a] rounded-xl border border-slate-800">
      <div className="text-sm font-semibold text-slate-200">{label}</div>
      <select
        value={data[key] as string}
        onChange={(e) => updateSettings(key, e.target.value)}
        className="bg-[#0e1422] border border-slate-700 text-blue-400 text-xs rounded-lg px-2 py-1.5 outline-none focus:border-blue-500"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="bg-[#0e1422] rounded-3xl p-5 border border-slate-800/80 shadow-lg">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-400" />
          البيانات والتخزين
        </h3>

        <div className="space-y-3">
          {renderSelect('تنزيل الوسائط تلقائياً', 'autoDownloadMedia', [
            { value: 'wifi', label: 'Wi-Fi فقط' },
            { value: 'cellular', label: 'Wi-Fi وبيانات الهاتف' },
            { value: 'never', label: 'مطلقاً (يدوي)' },
          ])}
          
          {renderSelect('جودة تحميل الصور والفيديو', 'mediaQuality', [
            { value: 'low', label: 'منخفضة (سريعة)' },
            { value: 'medium', label: 'متوسطة' },
            { value: 'high', label: 'عالية الدقة (HD)' },
          ])}

          <div className="flex items-center justify-between p-3 bg-[#141c2a] rounded-xl border border-slate-800">
            <div>
              <div className="text-sm font-semibold text-slate-200">توفير البيانات</div>
              <div className="text-[10px] text-slate-400 mt-1 max-w-[200px]">يقلل من استهلاك البيانات أثناء إجراء المكالمات وتصفح القصص.</div>
            </div>
            <button
              onClick={() => updateSettings('dataSaver', !data.dataSaver)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${data.dataSaver ? 'bg-blue-500' : 'bg-slate-700'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${data.dataSaver ? 'translate-x-1' : '-translate-x-6'}`} />
            </button>
          </div>
          
          <button onClick={handleClearCache} disabled={isClearing} className={`w-full mt-2 py-3 border border-slate-700/80 font-bold rounded-xl flex items-center justify-center gap-2 transition active:scale-95 ${clearedSuccess ? 'bg-emerald-500/10 text-emerald-400' : 'bg-[#141c2a] text-rose-400 hover:bg-rose-500/10 disabled:opacity-50'}`}>
            <HardDrive className="w-4 h-4" />
            {isClearing ? 'جاري المسح...' : clearedSuccess ? 'تم مسح الملفات بنجاح' : 'مسح الملفات المؤقتة (Cache)'}
          </button>
        </div>

        <button disabled={isSaving} onClick={handleSave} className={`mt-4 w-full py-2.5 font-bold rounded-xl flex items-center justify-center gap-2 transition active:scale-95 ${savedSuccess ? 'bg-blue-400 text-slate-900' : 'bg-blue-500 hover:bg-blue-400 text-slate-950 disabled:opacity-50'}`}>
          <Save className="w-4 h-4" />
          {isSaving ? 'جاري الحفظ...' : savedSuccess ? 'تم الحفظ' : 'حفظ إعدادات البيانات'}
        </button>
      </div>
    </div>
  );
};
